import type { QuickFindDestination, QuickFindSeeAll, SearchPortal } from './types';

/**
 * Where a Quick Find result may take somebody — the ONLY places.
 *
 * The server names a destination by a route KEY, never a URL; this map turns
 * the key into a path of this application's router
 * (`src/app/router/index.tsx`, each path checked there). A key this map does
 * not hold, a key of another portal, or a parameter that is missing, extra or
 * not an opaque public id is not followed: the result is shown without a link
 * rather than sent somewhere guessed. The record page re-authorises on arrival
 * (a stale or no-longer-visible record is that page's normal "not available").
 *
 * The keys follow the guidance engine's safe-route names, where one exists, so
 * one name means one place across the product.
 */
interface RecordRoute {
  portal: SearchPortal;
  /** A react-router pattern: static segments and `:id`. */
  path: string;
}

export const RECORD_ROUTES = {
  'tenant.catalogue-item': { portal: 'tenant', path: '/catalogue/:id' },
  'tenant.request': { portal: 'tenant', path: '/requests/:id' },
  'tenant.purchase-order': { portal: 'tenant', path: '/purchase-orders/:id' },
  // The SPA's RFQ record is `/sourcing/:id` (router); `/sourcing/rfqs/:id` is the API's.
  'tenant.rfq': { portal: 'tenant', path: '/sourcing/:id' },
  'tenant.supplier': { portal: 'tenant', path: '/suppliers/:id' },
  // The supplier's RFQ page takes the INVITATION id, which is what the family returns.
  'vendor.order': { portal: 'vendor', path: '/vendor/orders/:id' },
  'vendor.rfq': { portal: 'vendor', path: '/vendor/rfqs/:id' },
  'platform.organisation': { portal: 'platform', path: '/platform/organisations/:id' },
} as const satisfies Record<string, RecordRoute>;

/**
 * The lists a "See all in <list>" may open, each with the term in its own
 * `?search=` — the list's existing URL state, so Back returns to the overlay's
 * opener and the list keeps the term like any search typed there.
 */
export const LIST_ROUTES = {
  'tenant.catalogue': { portal: 'tenant', path: '/catalogue' },
  'tenant.my-requests': { portal: 'tenant', path: '/my/requests' },
  'tenant.purchase-orders': { portal: 'tenant', path: '/purchase-orders' },
  'tenant.sourcing': { portal: 'tenant', path: '/sourcing' },
  'tenant.suppliers': { portal: 'tenant', path: '/suppliers' },
  'vendor.orders': { portal: 'vendor', path: '/vendor/orders' },
  'vendor.rfqs': { portal: 'vendor', path: '/vendor/rfqs' },
  'platform.organisations': { portal: 'platform', path: '/platform/organisations' },
} as const satisfies Record<string, RecordRoute>;

export type RecordRouteKey = keyof typeof RECORD_ROUTES;
export type ListRouteKey = keyof typeof LIST_ROUTES;

/** An opaque public identifier: no `/`, `.`, `%`, `?`, `#` or whitespace. */
const PARAM_VALUE = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;

function own<T extends object>(table: T, key: string): key is Extract<keyof T, string> {
  return Object.hasOwn(table, key);
}

/**
 * The path for a result, or `null` when it cannot be followed safely from
 * this portal.
 */
export function destinationPath(
  portal: SearchPortal,
  destination: QuickFindDestination | null | undefined,
): string | null {
  if (!destination || typeof destination.route !== 'string') return null;
  if (!own(RECORD_ROUTES, destination.route)) return null;

  const route = RECORD_ROUTES[destination.route];

  if (route.portal !== portal) return null;

  const params = destination.params ?? {};
  const wanted = route.path
    .split('/')
    .filter((segment) => segment.startsWith(':'))
    .map((segment) => segment.slice(1));

  if (Object.keys(params).some((name) => !wanted.includes(name))) return null;

  const segments: string[] = [];

  for (const segment of route.path.split('/')) {
    if (!segment.startsWith(':')) {
      segments.push(segment);
      continue;
    }

    const value = Object.hasOwn(params, segment.slice(1)) ? params[segment.slice(1)] : undefined;

    if (typeof value !== 'string' || !PARAM_VALUE.test(value)) return null;

    segments.push(value);
  }

  return segments.join('/');
}

/**
 * The list a "See all" opens, with the term in that list's own `search`; or
 * `null` when the list is unknown, of another portal, or the term is empty.
 */
export function seeAllPath(
  portal: SearchPortal,
  seeAll: QuickFindSeeAll | null | undefined,
): string | null {
  if (!seeAll || typeof seeAll.route !== 'string') return null;
  if (!own(LIST_ROUTES, seeAll.route)) return null;

  const route = LIST_ROUTES[seeAll.route];

  if (route.portal !== portal) return null;

  const term = seeAll.query?.search;

  if (typeof term !== 'string' || term.trim() === '') return null;

  return `${route.path}?${new URLSearchParams({ search: term }).toString()}`;
}
